/** Full mock-exam ("Prüfungssimulation") — assembles one item per
 * objective Teil into a timed booklet, then scores it server-side and
 * shows a per-section breakdown with a 60% pass line. Reuses the same
 * format renderers as the single-item player. */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { ContentLocked } from "../../components/ContentLocked";
import { Badge, Button, Card, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";
import { answerSlots, FORMATS } from "./ExamItemPlayerPage";
import { Stopwatch } from "./Stopwatch";

type Answers = Record<string, Record<string, unknown>>;

export function bandKey(percent: number): MessageKey {
  if (percent >= 90) return "practice.exam.band.sehrGut";
  if (percent >= 75) return "practice.exam.band.gut";
  if (percent >= 60) return "practice.exam.band.bestanden";
  return "practice.exam.band.nichtBestanden";
}

function ResultView({
  result,
  onRetake,
  backLink,
}: {
  result: api.MockResult;
  onRetake: () => void;
  backLink: string;
}) {
  const t = useT();
  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Card>
        <div className="text-center">
          <p className="text-5xl" aria-hidden>{result.passed ? "🎓" : "💪"}</p>
          <h1 className="mt-2 text-3xl font-extrabold" data-testid="mock-score">
            {result.overall_percent}%
          </h1>
          <div className="mt-2">
            <Badge tone={result.passed ? "success" : "warning"}>
              {result.passed ? t("practice.exam.passed") : t("practice.exam.notPassed")}
              {" · "}
              {t(bandKey(result.overall_percent))}
            </Badge>
          </div>
          <p className="mt-2 text-sm text-muted">
            {result.total_correct} / {result.total_questions}
          </p>
        </div>

        <h2 className="mt-6 text-sm font-bold uppercase text-muted">{t("practice.exam.bySection")}</h2>
        <div className="mt-3 space-y-3">
          {result.sections.map((s) => (
            <div key={s.code}>
              <div className="flex justify-between text-sm">
                <span className="font-semibold text-ink">
                  <span aria-hidden>{s.icon}</span> {s.name}
                </span>
                <span className="text-muted">
                  {s.correct}/{s.total} · {s.percent}%
                </span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-canvas">
                <div
                  className={`h-2 rounded-full ${s.percent >= 60 ? "bg-success" : "bg-warning"}`}
                  style={{ width: `${s.percent}%` }}
                  aria-label={`${s.name} ${s.percent}%`}
                />
              </div>
            </div>
          ))}
        </div>

        <p className="mt-5 rounded-control bg-canvas p-3 text-xs text-muted">
          {t("practice.exam.note")}
        </p>

        <div className="mt-5 flex justify-center gap-3">
          <Button variant="secondary" onClick={onRetake}>{t("practice.exam.retake")}</Button>
          <Link to={backLink}><Button>{t("practice.backToPart")}</Button></Link>
        </div>
      </Card>
    </div>
  );
}

export function MockExamPage() {
  const t = useT();
  const { board = "", level = "" } = useParams();
  const [exam, setExam] = useState<api.MockExam | null>(null);
  const [history, setHistory] = useState<api.MockAttempt[]>([]);
  const [error, setError] = useState(false);
  const [lockedModule, setLockedModule] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<api.MockResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getMockExam(board, level)
      .then((row) => {
        if (!cancelled) setExam(row);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setLockedModule(err.requiredModule);
        } else {
          setError(true);
        }
      });
    // History is a nice-to-have — never block the exam on it.
    api
      .mockHistory(board, level)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [board, level]);

  const parts = useMemo(
    () => (exam?.sections ?? []).flatMap((s) => s.parts),
    [exam],
  );
  const totalQuestions = useMemo(
    () => parts.reduce((n, p) => n + answerSlots(p.item_type, p.item.payload), 0),
    [parts],
  );
  const answered = parts.reduce(
    (n, p) => n + Object.values(answers[p.item.id] ?? {}).filter((v) => v !== undefined).length,
    0,
  );

  const setAnswer = (itemId: string, id: string, value: unknown) =>
    setAnswers((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [id]: value } }));

  const onSubmit = async () => {
    setBusy(true);
    try {
      setResult(await api.submitMockExam(board, level, answers));
      window.scrollTo(0, 0);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const onRetake = () => {
    setResult(null);
    setStarted(false);
    setAnswers({});
    api.getMockExam(board, level).then(setExam).catch(() => setError(true));
    api.mockHistory(board, level).then(setHistory).catch(() => {});
  };

  const backLink = "/practice";

  if (lockedModule !== null) return <ContentLocked module={lockedModule} />;
  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{t("common.error.generic")}</p>
      </Card>
    );
  }
  if (!exam) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }
  if (result) {
    return <ResultView result={result} onRetake={onRetake} backLink={backLink} />;
  }

  // Intro / start screen.
  if (!started) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Link to={backLink} className="text-sm text-muted hover:text-brand">
          ← {t("practice.title")}
        </Link>
        <Card>
          <p className="text-4xl" aria-hidden>📝</p>
          <h1 className="mt-2 text-2xl font-extrabold">{exam.name}</h1>
          <p className="mt-2 text-muted">
            {t("practice.exam.introBody", {
              sections: exam.sections.length,
              minutes: exam.total_minutes,
              questions: totalQuestions,
            })}
          </p>

          {history.length > 0 && (
            <div className="mt-5">
              <h2 className="text-sm font-bold uppercase text-muted">{t("practice.exam.history")}</h2>
              <ul className="mt-2 space-y-1">
                {history.map((h) => (
                  <li
                    key={h.id}
                    className="flex items-center justify-between rounded-control border border-line px-3 py-1.5 text-sm"
                  >
                    <span className="text-muted">
                      {h.taken_at ? new Date(h.taken_at).toLocaleDateString() : ""}
                    </span>
                    <span className={`font-semibold ${h.passed ? "text-success" : "text-warning"}`}>
                      {h.overall_percent}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5">
            <Button onClick={() => setStarted(true)}>{t("practice.exam.begin")}</Button>
          </div>
        </Card>
      </div>
    );
  }

  // The exam booklet.
  const unanswered = Math.max(0, totalQuestions - answered);
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="sticky top-0 z-10 -mx-2 flex items-center justify-between rounded-control border border-line bg-surface/95 px-4 py-2 backdrop-blur">
        <div className="flex items-center gap-3">
          <Stopwatch running={!result} />
          <span className="text-xs text-muted">{t("practice.exam.allowed", { minutes: exam.total_minutes })}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${unanswered ? "text-warning" : "text-success"}`}>
            {unanswered ? t("practice.unanswered", { n: unanswered }) : t("practice.allAnswered")}
          </span>
          <Button onClick={onSubmit} loading={busy}>{t("practice.exam.finish")}</Button>
        </div>
      </div>

      {exam.sections.map((section) => (
        <section key={section.code} className="space-y-4">
          <h2 className="text-xl font-bold text-ink">
            <span aria-hidden>{section.icon}</span> {section.name}
          </h2>
          {section.parts.map((part) => {
            const Fmt = FORMATS[part.item_type];
            return (
              <Card key={part.item.id}>
                <h3 className="mb-1 text-sm font-bold text-brand">{part.title}</h3>
                <p className="mb-3 text-sm text-muted">{String(part.item.payload.instruction ?? "")}</p>
                {Fmt && (
                  <Fmt
                    payload={part.item.payload}
                    answers={answers[part.item.id] ?? {}}
                    setAnswer={(id, value) => setAnswer(part.item.id, id, value)}
                  />
                )}
              </Card>
            );
          })}
        </section>
      ))}

      <div className="flex justify-end">
        <Button onClick={onSubmit} loading={busy}>{t("practice.exam.finish")}</Button>
      </div>
    </div>
  );
}
